import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "../services/httpClient";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaSearch,
  FaCalendarAlt,
} from "react-icons/fa";

const AdminFeedback = () => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showImageLightbox, setShowImageLightbox] = useState(false);
  const [lightboxImageIndex, setLightboxImageIndex] = useState(0);
  const [imageErrors, setImageErrors] = useState({});

  // Filters and pagination
  const [emailSearch, setEmailSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const debounceTimerRef = useRef(null);
  const hasInitialFetch = useRef(false);
  const prevEmailSearchRef = useRef("");
  const prevPageRef = useRef(1);
  const prevStartDateRef = useRef("");
  const prevEndDateRef = useRef("");

  // Fetch feedback list
  const fetchFeedback = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        page_size: pageSize.toString(),
      });

      if (emailSearch.trim().length >= 3) {
        params.append("email", emailSearch.trim());
      }

      if (startDate) {
        // Set to start of day (00:00:00 UTC)
        const date = new Date(startDate);
        date.setUTCHours(0, 0, 0, 0);
        params.append("start_date", date.toISOString());
      }

      if (endDate) {
        // Set to end of day (23:59:59 UTC)
        const date = new Date(endDate);
        date.setUTCHours(23, 59, 59, 999);
        params.append("end_date", date.toISOString());
      }

      const response = await axios.get(`/v2/feedback?${params.toString()}`);

      if (response.data) {
        const items = Array.isArray(response.data.items)
          ? response.data.items
          : Array.isArray(response.data)
          ? response.data
          : [];

        setFeedbackList(items);
        setTotalPages(
          response.data.total_pages || response.data.totalPages || 1
        );
        setTotalCount(
          response.data.total_count || response.data.totalCount || items.length
        );
      }
    } catch (error) {
      console.error("Error fetching feedback:", error);
      toast.error("Failed to load feedback. Please try again.");
      setFeedbackList([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, emailSearch, startDate, endDate]);

  // Initial fetch on mount - only runs once
  useEffect(() => {
    if (!hasInitialFetch.current) {
      hasInitialFetch.current = true;
      prevEmailSearchRef.current = emailSearch;
      prevPageRef.current = currentPage;
      prevStartDateRef.current = startDate;
      prevEndDateRef.current = endDate;
      fetchFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Debounced search effect - wait 2 seconds after user stops typing
  // Only triggers when emailSearch actually changes (not on initial mount)
  useEffect(() => {
    // Skip if initial fetch hasn't happened yet
    if (!hasInitialFetch.current) {
      prevEmailSearchRef.current = emailSearch;
      return;
    }

    // Only proceed if emailSearch actually changed
    if (prevEmailSearchRef.current === emailSearch) {
      return;
    }

    prevEmailSearchRef.current = emailSearch;

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only search if email has 3+ characters or is empty (to show all)
    if (emailSearch.trim().length >= 3 || emailSearch.trim().length === 0) {
      debounceTimerRef.current = setTimeout(() => {
        setCurrentPage(1); // Reset to first page on search
        fetchFeedback();
      }, 2000); // 2 second debounce - wait for user to stop typing
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSearch]); // Only depend on emailSearch

  // Fetch on page change or date filter change (not email search - that's handled by debounce)
  useEffect(() => {
    // Skip if initial fetch hasn't happened yet (it will be handled by initial fetch effect)
    if (!hasInitialFetch.current) {
      return;
    }

    // Only fetch if values actually changed
    const pageChanged = prevPageRef.current !== currentPage;
    const startDateChanged = prevStartDateRef.current !== startDate;
    const endDateChanged = prevEndDateRef.current !== endDate;

    if (pageChanged || startDateChanged || endDateChanged) {
      prevPageRef.current = currentPage;
      prevStartDateRef.current = startDate;
      prevEndDateRef.current = endDate;
      fetchFeedback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, startDate, endDate]); // Removed fetchFeedback from deps to avoid extra calls

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return "N/A";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Handle row click
  const handleRowClick = (feedback) => {
    setSelectedFeedback(feedback);
    setShowDetailPanel(true);
    setImageErrors({}); // Clear image errors when selecting new feedback
  };

  // Handle image click in carousel
  const handleImageClick = (index) => {
    setLightboxImageIndex(index);
    setShowImageLightbox(true);
  };

  // Navigate lightbox images
  const navigateLightbox = (direction) => {
    if (!selectedFeedback?.images) return;
    const totalImages = selectedFeedback.images.length;
    if (direction === "next") {
      setLightboxImageIndex((prev) => (prev + 1) % totalImages);
    } else {
      setLightboxImageIndex((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  // Clear filters
  const handleClearFilters = () => {
    setEmailSearch("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Admin Feedback
        </h1>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Email Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search by Email
              </label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter email (min 3 chars)"
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>

            {/* Start Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>

            {/* End Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <div className="relative">
                <FaCalendarAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
              </div>
            </div>
          </div>

          {/* Clear Filters Button */}
          {(emailSearch || startDate || endDate) && (
            <div className="mt-4">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : feedbackList.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No feedback found
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Feedback Text
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feedbackList.map((feedback, index) => (
                      <tr
                        key={feedback.id || index}
                        onClick={() => handleRowClick(feedback)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {feedback.email || feedback.user_email || "N/A"}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {truncateText(
                            feedback.feedback_text || feedback.text,
                            150
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(
                            feedback.created_at || feedback.createdAt
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-700">
                  Showing page {currentPage} of {totalPages} ({totalCount}{" "}
                  total)
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <FaChevronLeft className="inline mr-1" />
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <FaChevronRight className="inline ml-1" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Detail Panel (Right Side) */}
      {showDetailPanel && selectedFeedback && (
        <div className="fixed inset-0 z-50 flex">
          {/* Overlay - Light blueish with transparency */}
          <div
            className="flex-1 bg-primary/20 backdrop-blur-sm"
            onClick={() => setShowDetailPanel(false)}
          />

          {/* Panel */}
          <div className="bg-white w-full md:w-1/2 lg:w-2/5 h-full flex flex-col shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b bg-gray-50">
              <h2 className="text-xl font-semibold text-gray-800">
                Feedback Details
              </h2>
              <button
                onClick={() => setShowDetailPanel(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors p-1 hover:bg-gray-100 rounded-full"
              >
                <FaTimes className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <p className="text-gray-900">
                    {selectedFeedback.email ||
                      selectedFeedback.user_email ||
                      "N/A"}
                  </p>
                </div>

                {/* Created At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created At
                  </label>
                  <p className="text-gray-900">
                    {formatDate(
                      selectedFeedback.created_at || selectedFeedback.createdAt
                    )}
                  </p>
                </div>

                {/* Feedback Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Feedback Text
                  </label>
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {selectedFeedback.feedback_text ||
                      selectedFeedback.text ||
                      "N/A"}
                  </p>
                </div>

                {/* Images Carousel */}
                {selectedFeedback.images &&
                  Array.isArray(selectedFeedback.images) &&
                  selectedFeedback.images.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Attached Images ({selectedFeedback.images.length})
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        {selectedFeedback.images.map((image, index) => {
                          // Use image directly like lightbox does - API should return proper format
                          // If it's not a data URL, try to format it
                          let imageSrc = image;

                          if (
                            !imageSrc ||
                            typeof imageSrc !== "string" ||
                            !imageSrc.trim()
                          ) {
                            return null;
                          }

                          // If it doesn't start with data:, try to add the prefix
                          if (!imageSrc.startsWith("data:")) {
                            // Detect image type from base64 string
                            let mimeType = "image/jpeg"; // default
                            if (imageSrc.startsWith("iVBORw0KGgo")) {
                              mimeType = "image/png";
                            } else if (
                              imageSrc.startsWith("/9j/") ||
                              imageSrc.startsWith("/9j/4AAQ")
                            ) {
                              mimeType = "image/jpeg";
                            } else if (imageSrc.startsWith("UklGR")) {
                              mimeType = "image/webp";
                            }
                            imageSrc = `data:${mimeType};base64,${imageSrc}`;
                          }

                          const imageKey = `${
                            selectedFeedback.id || index
                          }-${index}`;
                          const hasError = imageErrors[imageKey];

                          return (
                            <div
                              key={index}
                              onClick={() =>
                                !hasError && handleImageClick(index)
                              }
                              className="relative aspect-square border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:border-primary transition-colors group bg-white"
                            >
                              {hasError ? (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm p-2 text-center">
                                  Image failed to load
                                </div>
                              ) : (
                                <>
                                  <img
                                    src={imageSrc}
                                    alt={`Feedback image ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    style={{
                                      display: "block",
                                      backgroundColor: "#ffffff",
                                    }}
                                    onError={(e) => {
                                      console.error(
                                        "Thumbnail image load error:",
                                        imageSrc.substring(0, 100)
                                      );
                                      setImageErrors((prev) => ({
                                        ...prev,
                                        [imageKey]: true,
                                      }));
                                    }}
                                    onLoad={(e) => {
                                      console.log(
                                        "Thumbnail image loaded successfully:",
                                        index,
                                        "Image src length:",
                                        imageSrc.length
                                      );
                                      // Clear error if image loads successfully
                                      setImageErrors((prev) => {
                                        const newErrors = { ...prev };
                                        delete newErrors[imageKey];
                                        return newErrors;
                                      });
                                    }}
                                  />
                                  <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-20 transition-opacity flex items-center justify-center pointer-events-none">
                                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-medium">
                                      Click to enlarge
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {showImageLightbox && selectedFeedback?.images && (
        <div className="fixed inset-0 z-[60] bg-black bg-opacity-90 flex items-center justify-center p-4">
          <button
            onClick={() => setShowImageLightbox(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors p-2"
          >
            <FaTimes className="w-6 h-6" />
          </button>

          {/* Navigation Buttons */}
          {selectedFeedback.images.length > 1 && (
            <>
              <button
                onClick={() => navigateLightbox("prev")}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-2 bg-black bg-opacity-50 rounded-full"
              >
                <FaChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={() => navigateLightbox("next")}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:text-gray-300 transition-colors p-2 bg-black bg-opacity-50 rounded-full"
              >
                <FaChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Image */}
          <div className="max-w-5xl max-h-[90vh] flex flex-col items-center">
            <img
              src={selectedFeedback.images[lightboxImageIndex]}
              alt={`Feedback image ${lightboxImageIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {selectedFeedback.images.length > 1 && (
              <div className="mt-4 text-white text-sm">
                Image {lightboxImageIndex + 1} of{" "}
                {selectedFeedback.images.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;
